import { useVirtualizer } from '@tanstack/react-virtual';
import { forwardRef, memo, useImperativeHandle, useRef } from 'react';

export type PDFViewerOutlineVirtualizedListProps = {
  rowsCount: number;
  children: (props: { index: number }) => JSX.Element | null;
};

export const OUTLINE_PAGE_HEIGHT = 142;

export type PDFViewerOutlineVirtualizedListHandle = {
  scrollToIndex: (index: number) => void;
};

const _PDFViewerOutlineVirtualizedList = forwardRef<
  PDFViewerOutlineVirtualizedListHandle,
  PDFViewerOutlineVirtualizedListProps
>(({ rowsCount, children }: PDFViewerOutlineVirtualizedListProps, forwardedRef) => {
  const scrollElementRef = useRef<HTMLDivElement | null>(null);

  const virtualizer = useVirtualizer({
    count: rowsCount,
    estimateSize: () => OUTLINE_PAGE_HEIGHT,
    getScrollElement: () => scrollElementRef.current,
    overscan: 6,
    paddingEnd: 30,
    scrollMargin: 16,
  });

  useImperativeHandle(
    forwardedRef,
    () => ({
      scrollToIndex: (index: number) => {
        virtualizer.scrollToIndex(index);
      },
    }),
    [virtualizer],
  );

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div
      className="scrollbar scrollbar-track-gray-2 scrollbar-thumb-gray-7 [&:not(:hover)]:scrollbar-track-transparent w-full flex-1 overflow-y-auto"
      data-testid="PDF_VIEWER_OUTLINE"
      ref={scrollElementRef}
    >
      <div
        className="relative w-full"
        style={{
          height: virtualizer.getTotalSize(),
        }}
      >
        <div
          className="absolute top-0 left-0 w-full"
          style={{
            transform: `translateY(${virtualItems[0]?.start ?? 0 + virtualizer.options.scrollMargin}px)`,
          }}
        >
          {virtualItems.map((virtualRow) => (
            <div
              className="px-6 py-2"
              data-index={virtualRow.index}
              key={virtualRow.key}
              ref={virtualizer.measureElement}
              style={{ height: OUTLINE_PAGE_HEIGHT }}
            >
              {children({ index: virtualRow.index })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

_PDFViewerOutlineVirtualizedList.displayName = '_PDFViewerOutlineVirtualizedList';

export const PDFViewerOutlineVirtualizedList = memo(
  _PDFViewerOutlineVirtualizedList,
) as typeof _PDFViewerOutlineVirtualizedList;

PDFViewerOutlineVirtualizedList.displayName = 'PDFViewerOutlineVirtualizedList';
